import { useRef, useState } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';
import { submitContactInquiry } from '../../services/ContactService';
import styles from './ContactForm.module.css';

const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

export default function ContactForm() {
  const turnstileRef = useRef(null);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    const form = event.currentTarget;

    if (!turnstileSiteKey) {
      setStatus({ type: 'error', message: 'Verification is not configured.' });
      return;
    }
    if (!turnstileToken) {
      setStatus({ type: 'error', message: 'Please complete the verification and try again.' });
      return;
    }

    const formData = new FormData(form);
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await submitContactInquiry({
        source: 'm80',
        name: formData.get('name'),
        email: formData.get('email'),
        message: formData.get('message'),
        website: formData.get('website'),
        turnstileToken,
      });

      form.reset();
      setStatus({ type: 'success', message: response.message || 'Thanks. We will be in touch soon.' });
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Your message could not be sent. Please try again.' });
    } finally {
      setIsSubmitting(false);
      setTurnstileToken('');
      turnstileRef.current?.reset();
    }
  }

  return (
    <section className={`${styles.contact} contact-reveal reveal`} id="contact">
      <div className={styles.contactInfo}>
        <h2 className={styles.contactHeading}>Get In<br />Touch</h2>
        <p className={styles.contactDesc}>Questions about fit, upcoming pieces, or custom work are always welcome.</p>
        <div className={styles.contactBadge}>Replies in a few days</div>
      </div>
      <form className={styles.contactForm} onSubmit={handleSubmit} autoComplete="off" aria-busy={isSubmitting}>
        <div className={styles.formRow}><label className={styles.formLabel} htmlFor="id_name">Name</label><input type="text" id="id_name" name="name" placeholder="Your name" required /></div>
        <div className={styles.formRow}><label className={styles.formLabel} htmlFor="id_email">Email</label><input type="email" id="id_email" name="email" placeholder="Your email" required /></div>
        <div className={`${styles.formRow} ${styles.formRowGrow}`}><label className={`${styles.formLabel} ${styles.formLabelTop}`} htmlFor="id_payload">Message</label><textarea id="id_payload" name="message" rows="5" placeholder="Tell us what you are looking for" required></textarea></div>
        <div className={styles.honeypot} aria-hidden="true"><label htmlFor="id_website">Website</label><input type="text" id="id_website" name="website" tabIndex="-1" autoComplete="off" /></div>
        <div className={styles.verificationRow}>
          <span className={styles.verificationLabel}>Verification</span>
          <div className={styles.verificationWidget}>
            {turnstileSiteKey ? (
              <Turnstile
                ref={turnstileRef}
                siteKey={turnstileSiteKey}
                onSuccess={setTurnstileToken}
                onExpire={() => setTurnstileToken('')}
                onError={() => {
                  setTurnstileToken('');
                  setStatus({ type: 'error', message: 'Verification could not load. Please try again.' });
                }}
                options={{ theme: 'light', size: 'flexible', action: 'contact' }}
              />
            ) : (
              <span className={styles.verificationUnavailable}>Add the Turnstile site key to enable this form.</span>
            )}
          </div>
        </div>
        <button type="submit" className={styles.formSubmit} disabled={isSubmitting || !turnstileToken}>
          {isSubmitting ? 'Sending...' : 'Send message'}
        </button>
        <p className={`${styles.formResponse} ${status.type === 'error' ? styles.formError : ''}`} role={status.type === 'error' ? 'alert' : undefined} aria-live="polite">{status.message}</p>
      </form>
    </section>
  );
}